
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

interface HeroSectionProps {
  scrollY: number;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  heroRef: React.RefObject<HTMLElement>;
}

const HeroSection: React.FC<HeroSectionProps> = ({ scrollY, searchQuery, setSearchQuery, heroRef }) => {
  return (
    <section
      ref={heroRef}
      className="relative overflow-hidden bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white"
      style={{ minHeight: "90vh" }}
    >
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?q=80&w=2400&auto=format&fit=crop&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: `translateY(${scrollY * 0.2}px)`,
        }}
      />

      {/* Hero background image behind first sentences */}
      <div
        className="absolute top-0 left-0 right-0 h-[60%] opacity-40 z-0"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?q=80&w=2400&auto=format&fit=crop&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D')",
          backgroundSize: "cover",
          backgroundPosition: "center top",
          maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 60%, rgba(0,0,0,0))",
          WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 60%, rgba(0,0,0,0))",
        }}
      />

      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-purple-900/90" />

      <div className="container mx-auto px-4 py-20 relative z-10 flex flex-col items-center justify-center h-full">
        <div className="max-w-4xl mx-auto text-center mb-12">
          <Badge className="mb-4 bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm">
            Revolutionizing AI Agents
          </Badge>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            Discover, Deploy, and{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-300 to-pink-300">Create</span>{" "}
            AI Agents
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-purple-100 max-w-3xl mx-auto">
            The marketplace for AI agents created by developers and enthusiasts around the world
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-purple-700 hover:bg-purple-100 px-8 shadow-lg transition-all duration-300 hover:shadow-xl" 
              asChild
            >
              <Link to="/browse" className="flex items-center gap-2">
                Browse Agents
                <ChevronRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="bg-white text-purple-700 hover:bg-purple-100 px-8 shadow-lg transition-all duration-300 hover:shadow-xl" 
              asChild
            >
              <Link to="/create-agent" className="flex items-center gap-2">
                Create Your Agent
                <ChevronRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>

        <div className="relative w-full max-w-3xl mx-auto mt-8">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full blur-3xl opacity-20" />
          <div className="relative bg-white/10 backdrop-blur-md rounded-full p-1.5">
            <div className="flex items-center">
              <Search className="absolute left-4 text-white/70" size={20} />
              <Input
                placeholder="Search for AI agents..."
                className="pl-12 py-6 text-lg bg-transparent border-none text-white placeholder:text-white/70 focus-visible:ring-0 focus-visible:ring-offset-0"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Button 
                className="absolute right-1.5 bg-purple-600 hover:bg-purple-700 text-white transition-all duration-200 shadow-sm hover:shadow-md"
              >
                Search
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 flex justify-center">
          <div className="animate-bounce mb-8">
            <ChevronRight className="h-8 w-8 text-white/70 rotate-90" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
